import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/services", label: "Services", id: "services" },
  { to: "/process", label: "Process", id: "process" },
  { to: "/about", label: "About", id: "about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || open
          ? "border-slate-800/80 bg-canvas/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                  isActive ? "text-cad" : "text-slate-400 hover:text-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/quote"
            data-testid="header-quote-button"
            className="group flex items-center gap-1.5 rounded-sm bg-laser px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas shadow-[0_0_24px_rgba(245,158,11,0.25)] transition-colors duration-200 hover:bg-amber-400"
          >
            Request a Quote
            <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/10 text-slate-200 md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-800/80 bg-canvas/95 backdrop-blur-xl md:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className={({ isActive }) =>
                    `rounded-sm px-2 py-3 font-mono text-sm uppercase tracking-[0.2em] transition-colors ${
                      isActive ? "text-cad" : "text-slate-300 hover:text-slate-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                data-testid="mobile-quote-button"
                className="mt-3 flex items-center justify-center gap-2 rounded-sm bg-laser px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas"
              >
                Request a Quote <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
